import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@sunfish/lib/utils';

const alertVariants = cva(
	'relative w-full rounded-lg border px-4 pl-5 py-3 text-sm [&>svg]:absolute [&>svg]:left-4 [&>svg]:size-4 [&>svg]:top-1/2 [&>svg]:translate-y-[-50%] [&>svg]:text-foreground [&>svg~*]:pl-7',
	{
		variants: {
			variant: {
				info: 'bg-info/20 border-info/50 text-info dark:border-info [&>svg]:text-info',
				default: 'bg-background text-foreground',
				brand: 'bg-brand/10 border-brand dark:border-brand [&>svg]:text-brand',
				positive: 'bg-positive/10 border-positive/50 text-positive dark:border-positive [&>svg]:text-positive',
				warning: 'bg-warning/10 border-warning/50 text-warning dark:border-warning [&>svg]:text-warning',
				destructive:
					'bg-destructive/10 border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

const Alert = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
	<div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, ...props }, ref) => (
		<h5 ref={ref} className={cn('mb-1 text-lg font-medium leading-none tracking-tight', className)} {...props} />
	)
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
	)
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertDescription, AlertTitle };
